import {
  Button,
  Card,
  Heading,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Text,
} from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { PolicyStateFeedback, type PolicyResourceState } from "../policy-state";
import type { PolicyVersionPlacement } from "../policy-version-records";

export type PolicyDivisionStandingProps = {
  readonly placements: PolicyResourceState<{
    readonly rows: readonly PolicyVersionPlacement[];
    readonly loadingRanks: boolean;
  }>;
  readonly onRetry: (() => void) | undefined;
  readonly onOpenLeague: (id: string) => void;
};

export function PolicyDivisionStanding({
  placements,
  onRetry,
  onOpenLeague,
}: PolicyDivisionStandingProps) {
  return (
    <Card>
      <Stack width="auto">
        <Heading level={2}>Player division standing</Heading>
        <Text size="small" tone="muted">
          Division rank and score combine this player's versions in the
          division. Version score above measures this version alone.
        </Text>
        <PolicyStateFeedback
          state={placements}
          subject="placements"
          onRetry={onRetry}
        />
        {placements.status === "ready" &&
          (placements.rows.length === 0 ? (
            <Text tone="muted">No active league placements.</Text>
          ) : (
            <div {...stylex.props(styles.tableFrame)}>
              <TableContainer aria-label="Policy version league placements">
                <div {...stylex.props(styles.placements)}>
                  <Table density="compact">
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>League</TableHeaderCell>
                        <TableHeaderCell>Division</TableHeaderCell>
                        <TableHeaderCell align="end">
                          Player rank
                        </TableHeaderCell>
                        <TableHeaderCell align="end">
                          Player score
                        </TableHeaderCell>
                        <TableHeaderCell align="end">Rounds</TableHeaderCell>
                        <TableHeaderCell>Placed</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {placements.rows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="quiet"
                              onClick={() => onOpenLeague(row.leagueId)}
                            >
                              {row.leagueName}
                            </Button>
                          </TableCell>
                          <TableCell>{row.divisionName}</TableCell>
                          <TableCell align="end">
                            {row.rank === null ? "Unranked" : `#${row.rank}`}{" "}
                            {row.rankAdornment}
                          </TableCell>
                          <TableCell align="end">{row.score}</TableCell>
                          <TableCell align="end">{row.rounds ?? "—"}</TableCell>
                          <TableCell>
                            {row.champion && (
                              <Text size="small">Champion · </Text>
                            )}
                            {row.placedAt}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TableContainer>
            </div>
          ))}
        {placements.status === "ready" && placements.loadingRanks && (
          <output>
            <Text size="small" tone="muted">
              Loading ranks…
            </Text>
          </output>
        )}
      </Stack>
    </Card>
  );
}

const styles = stylex.create({
  placements: { minWidth: "540px" },
  tableFrame: { minWidth: 0 },
});
